import { Component, ContextType, useContext } from 'react';
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react';
import { EffectFade, Navigation, Pagination } from 'swiper/modules';

import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

import galleryStyles from '../../article.module.css';

import { ArticleContext } from '../../Article.context';
import { VirtualCaption } from './Caption';
import { Credit } from './Image';
import { AppContext } from '../../../../App.context';

export class VirtualGallery extends Component<GalleryProps, any> {
	static contextType = ArticleContext;
	declare context: ContextType<typeof ArticleContext>;

	public static defaultProps = {
		id: '',
		caption: '',
		captionEnabled: false,
		creditEnabled: false,
		direction: 'horizontal',
		controlSpeed: 'medium',
	};

	interval: any = null;
	state: any = {
		index: null,
		swiper: null,
	};
	componentDidMount() {
		const { images, autoplay, controlSpeed } = this.props;
		const total = images.length;
		this.setState({
			index: 0,
			total,
		});
		if (autoplay) {
			let speed = 0;
			switch (controlSpeed) {
				case 'slow':
					speed = 11000;
					break;
				case 'medium':
					speed = 7000;
					break;
				case 'fast':
					speed = 3000;
					break;
				case 'vfast':
					speed = 1500;
					break;
				default:
					speed = 5000;
					break;
			}

			this.interval = setInterval(this.onTic, speed);
		}
	}

	componentDidUpdate(prevProps: GalleryProps, prevState: any) {
		const { swiper } = this.state;

		if (swiper && prevProps.direction !== this.props.direction) {
			swiper.changeDirection(this.props.direction);
		}

		if (prevState.swipper === null && swiper) {
			console.log(`I trigger the re render`);
			setTimeout(() => {
				swiper.updateSize();
			}, 500);
		}
	}

	componentWillUnmount() {
		if (this.interval) {
			clearInterval(this.interval);
		}
	}

	onTic = () => {
		const { autoplay } = this.props;
		const { total, swiper } = this.state;
		let { index } = this.state;
		if (!swiper) {
			return;
		}
		index = index + 1;
		if (!autoplay) {
			return;
		}

		if (index >= total) {
			this.setState({ index: 0 });
			swiper.slideTo(0, 200);
			return;
		}
		this.setState({ index });
		swiper.slideNext(200);
	};

	onSlideChange = ({ realIndex }: SwiperClass) => {
		this.setState({ index: realIndex });
	};

	render() {
		const {
			id,
			role,
			images,
			captionenabled,
			caption,
			creditenabled,
			credit,
			animation,
			bleed,
			styles,
			direction,
		} = this.props;

		const captionEnabled = captionenabled === 'on';
		const creditEnabled = creditenabled === 'on';

		const captionStyle: any = {};

		const { lang } = this.context;

		const dependencies = [Navigation, Pagination];
		if (animation === 'fade') {
			dependencies.push(EffectFade);
		}
		const { index } = this.state;
		const className = [galleryStyles['gallery'], 'media', 'gallery'];
		if (bleed === 'on') {
			className.push('bleed');
		}

		let containerStyle = {};
		if (styles) {
			containerStyle = applyStyles(styles, containerStyle);
		}

		if (role === 'mosaic') {
			return (
				<Mosaic
					id={id}
					images={images}
					captionEnabled={captionEnabled}
					caption={caption}
					creditEnabled={creditEnabled}
					credit={credit}
					bleed={bleed === 'on'}
				/>
			);
		}

		return (
			<div id={id} style={containerStyle} className={className.join(' ')}>
				<Swiper
					effect={animation}
					modules={dependencies}
					navigation={true}
					slidesPerView={'auto'}
					updateOnWindowResize={true}
					pagination={{
						clickable: true,
					}}
					onSlideChange={this.onSlideChange}
					onSwiper={(swiper: SwiperClass) => {
						swiper.changeDirection(direction);
						setTimeout(() => {
							swiper.update();
						}, 1000);
						this.setState({
							swiper,
						});
					}}
				>
					{images.map((image, i) => {
						const { imageurl, caption } = image;
						const slideClassesName = [
							galleryStyles['gallery-slider'],
						];
						if (i !== index) {
							slideClassesName.push(galleryStyles['hide']);
						}

						let captionText = '';
						if (typeof caption === 'string') {
							captionText = caption;
						} else if (typeof caption === 'object') {
							captionText = caption[lang];
						}

						return (
							<SwiperSlide
								key={i}
								virtualIndex={i}
								className={slideClassesName.join(' ')}
							>
								<div className={galleryStyles['gallery-image']}>
									<img src={imageurl} alt={captionText} />
									<VirtualCaption content={caption} />
									{creditEnabled && credit ? (
										<Credit
											style={captionStyle}
											content={credit}
										/>
									) : null}
								</div>
							</SwiperSlide>
						);
					})}
				</Swiper>
				{captionEnabled && caption ? (
					<VirtualCaption content={caption} />
				) : null}
			</div>
		);
	}
}

interface GalleryProps {
	id: string;
	images: Array<any>;
	styles: any;
	captionEnabled: boolean;
	caption?: string;
	creditEnabled?: boolean;
	credit?: string;
	expandFullWidth: boolean;
	animation: any;
	autoplay: boolean;
	captionenabled: 'on' | 'off';
	creditenabled: 'on' | 'off';
	bleed: 'on' | 'off';
	direction: 'horizontal' | 'vertical' | undefined;
	controlSpeed: string;
	role: 'gallery' | 'mosaic';
}

function Mosaic({
	id,
	images,
	captionEnabled,
	caption,
	bleed,
	credit,
	creditEnabled,
}: MosaicProps) {
	const { lang } = useContext(AppContext);
	const classNames = ['media', 'gallery', galleryStyles['gallery']];
	let captionContent = '';
	if (caption && lang) {
		captionContent =
			typeof caption === 'string' ? caption : caption[`${lang}`];
	}

	if (bleed) {
		classNames.push('bleed');
	}

	if (!images.length) {
		return null;
	}

	return (
		<div id={id} className={classNames.join(' ')}>
			<div className={galleryStyles['mosaic-container']}>
				{images.map(({ imageurl }) => (
					<MosaicTile key={imageurl} url={imageurl} />
				))}
			</div>
			{captionEnabled && caption ? (
				<VirtualCaption content={captionContent} />
			) : null}
			{creditEnabled && credit ? (
				<Credit content={credit} style={{}} />
			) : null}
		</div>
	);
}

interface MosaicProps {
	id: string;
	images: Array<any>;
	captionEnabled: boolean;
	caption?: string;
	creditEnabled?: boolean;
	credit?: string;
	bleed: boolean;
}

function MosaicTile({ url, caption }: MosaicTileProps) {
	return (
		<div className={galleryStyles['tile']}>
			<img src={url} alt={caption} />
		</div>
	);
}

interface MosaicTileProps {
	url: string;
	caption?: string;
}

function applyStyles(styles: any, containerStyle: any) {
	containerStyle = clone(containerStyle);
	styles = clone(styles);
	const {
		unit,
		margin_bottom,
		margin_top,
		margin_left,
		margin_right,
		padding_left,
		padding_right,
		padding_top,
		padding_bottom,
	} = styles;
	if (margin_bottom !== undefined && unit['margin_bottom']) {
		containerStyle.marginBottom = `${margin_bottom}${unit['margin_bottom']}`;
	}
	if (margin_top !== undefined && unit['margin_top']) {
		containerStyle.marginTop = `${margin_top}${unit['margin_top']}`;
	}
	if (margin_left !== undefined && unit['margin_left']) {
		containerStyle.marginLeft = `${margin_left}${unit['margin_left']}`;
	}
	if (margin_right !== undefined && unit['margin_right']) {
		containerStyle.marginRight = `${margin_right}${unit['margin_right']}`;
	}

	if (padding_bottom !== undefined && unit['padding_bottom']) {
		containerStyle.paddingBottom = `${padding_bottom}${unit['padding_bottom']}`;
	}
	if (padding_top !== undefined && unit['padding_top']) {
		containerStyle.paddingTop = `${padding_top}${unit['padding_top']}`;
	}
	if (padding_left !== undefined && unit['padding_left']) {
		containerStyle.paddingLeft = `${margin_left}${unit['padding_left']}`;
	}
	if (padding_right !== undefined && unit['padding_right']) {
		containerStyle.paddingRight = `${margin_right}${unit['padding_right']}`;
	}

	return containerStyle;
}

function clone(obj: any) {
	if (structuredClone) {
		return structuredClone(obj);
	}
	return obj ? JSON.parse(JSON.stringify(obj)) : obj;
}
